/**
 * RENDER — chase camera rig. The single biggest lever on "feel".
 *
 * Goals from the design direction:
 *   - Sits behind & above the marble, FORWARD-LOCKED to world +Z and looking AHEAD
 *     down +Z so left/right matches the finger with no swing (pairs with the
 *     direct-lateral lane-shifter steering).
 *   - Critically-damped, framerate-independent smoothing: enough lag to feel weighty,
 *     no overshoot, no spring bounce, settles fast → "tight, never floaty".
 *   - Speed → FOV (widen) + pull-back + more look-ahead. This is the primary, cheapest
 *     speed cue and the thing that makes it read as FAST on a phone.
 *
 * Everything derives from the interpolated marble position + the GameState velocity
 * (speed only). No input, no physics. Allocation-free per frame.
 */

import * as THREE from 'three';
import type { Vec3 } from '@/types';
import { FEEL } from './theme';
import { dampFactor, mapClamped, lenVec3 } from './math';

export class ChaseCamera {
  readonly camera: THREE.PerspectiveCamera;

  private readonly maxSpeed: number;

  // Smoothed state (world space). Initialized on first `snapTo`.
  private readonly desiredPos = new THREE.Vector3();
  private readonly desiredLook = new THREE.Vector3();
  private readonly smoothPos = new THREE.Vector3();
  private readonly smoothLook = new THREE.Vector3();
  private smoothFov: number;

  // Scratch (reused; no per-frame allocation).
  private readonly back = new THREE.Vector3();
  private readonly up = new THREE.Vector3(0, 1, 0);
  private readonly tmpOffset = new THREE.Vector3();

  // Transient additive offsets from FX (e.g. near-miss kick). Decays externally.
  private readonly kick = new THREE.Vector3();
  private fovKick = 0;

  /**
   * Camera heading (unit XZ, y=0). FORWARD-LOCKED to world +Z and never updated
   * per frame: the camera sits behind the marble along -Z and looks ahead along
   * +Z, so on-screen left/right matches the player's finger with no swing. This
   * pairs with the direct-lateral lane-shifter steering. Set to +Z on `snapTo`.
   */
  private readonly heading = new THREE.Vector3(0, 0, 1);

  private initialized = false;

  constructor(aspect: number, maxSpeed: number) {
    this.maxSpeed = maxSpeed > 0 ? maxSpeed : 1;
    this.camera = new THREE.PerspectiveCamera(FEEL.fovIdle, aspect, 0.1, 500);
    this.smoothFov = FEEL.fovIdle;
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /**
   * Add a transient positional kick (e.g. near-miss). The renderer decays the FX
   * envelope and passes the current magnitude; we just store the world-space offset.
   */
  applyKick(offset: Vec3): void {
    this.kick.set(offset[0], offset[1], offset[2]);
  }

  /** Add a transient FOV kick in degrees (e.g. booster). Cleared as FX decays. */
  applyFovKick(deg: number): void {
    this.fovKick = deg;
  }

  /**
   * Recompute the camera for this frame.
   * @param marblePos  interpolated marble world position.
   * @param velocity   GameState velocity (world units/sec).
   * @param dt         render frame delta (seconds).
   */
  update(marblePos: Vec3, velocity: Vec3, dt: number): void {
    const speed = lenVec3(velocity);
    const speedFrac = mapClamped(speed, 0, this.maxSpeed, 0, 1);

    // Heading is FORWARD-LOCKED to world +Z (see `this.heading`); never updated per
    // frame, so the view never swings and left/right tracks the finger directly.

    // Desired position: behind the marble along its heading, raised up. Pull back
    // further with speed so more of the track ahead enters frame.
    const dist = FEEL.camDistIdle + FEEL.camDistSpeedGain * speedFrac;
    this.back.copy(this.heading).multiplyScalar(-dist);
    this.tmpOffset.copy(this.back).addScaledVector(this.up, FEEL.camHeight);
    this.desiredPos.set(marblePos[0], marblePos[1], marblePos[2]).add(this.tmpOffset);

    // Desired look target: ahead of the marble along heading; reaches further at speed.
    const ahead = FEEL.lookAheadIdle + FEEL.lookAheadSpeedGain * speedFrac;
    this.desiredLook
      .set(marblePos[0], marblePos[1], marblePos[2])
      .addScaledVector(this.heading, ahead);

    if (!this.initialized) {
      this.smoothPos.copy(this.desiredPos);
      this.smoothLook.copy(this.desiredLook);
      this.initialized = true;
    } else {
      // Framerate-independent critical damping toward the desired pose.
      const posT = dampFactor(FEEL.camPosSmoothing, dt);
      const lookT = dampFactor(FEEL.camLookSmoothing, dt);
      this.smoothPos.lerp(this.desiredPos, posT);
      this.smoothLook.lerp(this.desiredLook, lookT);
    }

    // Apply transient FX kick on top of the smoothed pose (already decayed by caller).
    this.camera.position.copy(this.smoothPos).add(this.kick);
    this.camera.up.copy(this.up);
    this.camera.lookAt(this.smoothLook);

    // Speed → FOV, smoothed so step changes in speed feel weighty, plus FX kick.
    const targetFov = FEEL.fovIdle + (FEEL.fovMax - FEEL.fovIdle) * speedFrac;
    const fovT = dampFactor(FEEL.fovSmoothing, dt);
    this.smoothFov += (targetFov - this.smoothFov) * fovT;
    const appliedFov = this.smoothFov + this.fovKick;
    if (Math.abs(this.camera.fov - appliedFov) > 1e-3) {
      this.camera.fov = appliedFov;
      this.camera.updateProjectionMatrix();
    }
  }

  /** Snap instantly to the ideal pose for `marblePos` (use on (re)spawn / build). */
  snapTo(marblePos: Vec3): void {
    this.heading.set(0, 0, 1);
    this.back.copy(this.heading).multiplyScalar(-FEEL.camDistIdle);
    this.tmpOffset.copy(this.back).addScaledVector(this.up, FEEL.camHeight);
    this.smoothPos.set(marblePos[0], marblePos[1], marblePos[2]).add(this.tmpOffset);
    this.smoothLook
      .set(marblePos[0], marblePos[1], marblePos[2])
      .addScaledVector(this.heading, FEEL.lookAheadIdle);
    this.smoothFov = FEEL.fovIdle;
    this.kick.set(0, 0, 0);
    this.fovKick = 0;
    this.initialized = true;
    this.camera.position.copy(this.smoothPos);
    this.camera.up.copy(this.up);
    this.camera.lookAt(this.smoothLook);
    this.camera.fov = FEEL.fovIdle;
    this.camera.updateProjectionMatrix();
  }
}
